import React, { useState, useRef, useEffect } from 'react';
import { useOrganization } from '../../contexts/OrganizationContext';
import './ColorPickerWithPresets.css';

/**
 * ColorPicker avec presets des couleurs de l'organisation
 * @param {string} value - Valeur de la couleur (format hex)
 * @param {function} onChange - Callback appelé lors du changement de couleur
 * @param {string} label - Label optionnel
 * @param {boolean} disabled - Désactiver le picker
 */
function ColorPickerWithPresets({ value = '#000000', onChange, label, disabled = false }) {
    const { primaryColor, secondaryColor, loading } = useOrganization();
    const [showPicker, setShowPicker] = useState(false);
    const [inputValue, setInputValue] = useState(value);
    const containerRef = useRef(null);

    // Synchroniser inputValue avec value externe
    useEffect(() => {
        setInputValue(value);
    }, [value]);

    // Fermer le picker au clic extérieur
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setShowPicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleColorChange = (e) => {
        const newColor = e.target.value;
        setInputValue(newColor);
        onChange?.(newColor);
    };

    const handleInputChange = (e) => {
        let newValue = e.target.value;

        // Ajouter # si manquant
        if (newValue && !newValue.startsWith('#')) {
            newValue = '#' + newValue;
        }

        setInputValue(newValue);

        // Valider le format hex complet avant d'appliquer
        if (/^#[0-9A-Fa-f]{6}$/.test(newValue)) {
            onChange?.(newValue);
        }
    };

    const handlePresetClick = (color) => {
        setInputValue(color);
        onChange?.(color);
        setShowPicker(false);
    };

    const hasOrgColors = !loading && (primaryColor || secondaryColor);

    return (
        <div className="color-picker-with-presets" ref={containerRef}>
            {label && <label className="color-picker-label">{label}</label>}

            <div className="color-picker-control">
                <button
                    type="button"
                    className="color-swatch-button"
                    style={{ backgroundColor: value }}
                    onClick={() => !disabled && setShowPicker(!showPicker)}
                    disabled={disabled}
                    title="Cliquer pour choisir une couleur"
                />

                <input
                    type="text"
                    className="color-text-input"
                    value={inputValue}
                    onChange={handleInputChange}
                    disabled={disabled}
                    maxLength={7}
                    placeholder="#000000"
                />
            </div>

            {showPicker && !disabled && (
                <div className="color-picker-dropdown">
                    {hasOrgColors && (
                        <div className="color-presets-section">
                            <span className="presets-label">Couleurs de l'organisation</span>
                            <div className="color-presets">
                                {primaryColor && (
                                    <button
                                        type="button"
                                        className={`preset-button ${value === primaryColor ? 'active' : ''}`}
                                        style={{ backgroundColor: primaryColor }}
                                        onClick={() => handlePresetClick(primaryColor)}
                                        title={`Primaire: ${primaryColor}`}
                                    >
                                        <span className="preset-tooltip">Primaire</span>
                                    </button>
                                )}
                                {secondaryColor && (
                                    <button
                                        type="button"
                                        className={`preset-button ${value === secondaryColor ? 'active' : ''}`}
                                        style={{ backgroundColor: secondaryColor }}
                                        onClick={() => handlePresetClick(secondaryColor)}
                                        title={`Secondaire: ${secondaryColor}`}
                                    >
                                        <span className="preset-tooltip">Secondaire</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="color-picker-native">
                        <input
                            type="color"
                            value={value}
                            onChange={handleColorChange}
                            className="native-color-input"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default ColorPickerWithPresets;
